/**
 * File Name          : datasnap-device.model.ts
 * Author             : Jared Laine Dyreson
 * Version            : 0.1.0
 * Date               : 2026-03-18
 * Description        : Short Description (manual edit required)
 * 
 * Copyright (C) Innovative Design Labs, Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * This file is subject to the terms and conditions defined in
 * file 'IDL_PROJECTTECH_LICENSE.txt', which is part of this source code package.
 */

import { Capacitor } from "@capacitor/core";
import { BehaviorSubject, Subject, Subscription, timer } from "rxjs";
import { DeviceFileSubscription } from "../../../projects/idl-physens/src/lib/models/subscription/device-file-sub.model";
import { ParsedValue, ParsedValues, parseSNSBytes } from "../../../projects/idl-physens/src/lib/utilities/physens-parser";
import { FILERECORDER_STATE, PACKET_SUB_TYPE, Publisher } from "../../../projects/idl-physens/src/lib/models";
import { DatasnapAnalysisService } from "../services/datasnap-analysis.service";
import { ImpactResults } from "../lesson-utils/impact-analysis";
import { Drive_Item, DRIVE_ITEM_TYPE } from "../../../projects/idl-physens/src/lib/models/file_system/drive-item.model";
import { PhysensBleDevice } from "../../../projects/idl-physens/src/lib/models/physens-devices/physens-ble-device.model";
import { ConnectionStatistics, Device_State, PORT_UUID } from "../../../projects/idl-physens/src/lib/models/physens-devices/physens-device.model";
import { AirRacerResults } from "../lesson-utils/air_racer-analysis";
import { ProjectileResults } from "../lesson-utils/projectile-analysis";
import { RWUpdateServerService } from "../services/rwupdate-server.service";
import { Datasnap_MK1 } from "../../../projects/idl-physens/src/lib/app-impls/datasnap-mk1";
import { ConsoleCaptureService } from "../services/console-capture.service";
import { BLE_CHARGE_ICON, isChargingState } from "../../../projects/idl-physens/src/lib/models/battery_state_char.model";

const DATASNAP_DEVICE_COLORS = [
    {red: 255, green:255, blue:0},
    {red: 0, green:255, blue:255},
    {red: 255, green:0, blue:255},
    {red: 255, green:128, blue:0},
    {red: 128, green:255, blue:0},
    {red: 0, green:255, blue:128},
    {red: 0, green:128, blue:255},
    {red: 128, green:0, blue:255},
    {red: 255, green:0, blue:128},
]

interface DatasnapData {
    values: DatasnapDatum[];
}

interface DatasnapDatum {
    sampleTime_uS: number;
    [key: string]: number; //key: <pubId>_<outputname>_<outputvectoridx(if nonzero)>
    //example:
    //2017_Ax: number;
    //2017_Ay: number;
    //2017_Az: number;
    //2017_Gx: number;
    //2017_Gy: number;
    //2017_Gz: number;
    //8212_Mx: number;
    //8212_My: number;
    //8212_Mz: number;
    //12544_HGFAx: number;
    //..etc...
}

export class DatasnapDevice {
    private static readonly RECORDING_CALL_TIMEOUT_MS = 10 * 60 * 1000;

    status: DatasnapDeviceStatus = DatasnapDeviceStatus.IDLE;
    
    private device: PhysensBleDevice | null = null;

    //Device Accessors
    get hasDevice(): boolean { return this.device != null; }
    get deviceName(): string | undefined { return this.device?.deviceName};
    get deviceId(): string | undefined { return this.device?.deviceId};
    get deviceState(): Device_State | undefined { return this.device?.deviceState};
    get deviceInfo() { return this.device?.device_info};
    get deviceTime() { return this.device?.deviceTime};
    get batteryState() { return this.device?.battery_state ?? BLE_CHARGE_ICON.NA};
    get mcumgr() { return this.device?.mcumgr};
    get deviceStateSubject() { return this.device?.deviceStateSubject};
    get needsReselect() { return this.device?.reconnectRequiresUserReselect.value ?? false}

    setDeviceNull() {
        this.device = null;
    }
    callRemoveDevice() {
        this.device?.remove();
    }

    // Issue one clean reconnect the instant an OTA completes and the device is physically back
    // (see PhysensBleDevice.reconnectAfterOtaSuccess). If the reconnect can't reach the device
    // (ChromeOS address rotation post-reboot), the "Reconnect Device" prompt appears so the user
    // re-pairs with a single tap.
    reconnectAfterOtaSuccess() {
        this.device?.reconnectAfterOtaSuccess();
    }

    // Silent, event-driven reconnect attempt (watchAdvertisements/getDevices where supported)
    // used while waiting for the device to come back post-OTA-reset (see
    // PhysensBleDevice.attemptSilentPostOtaReconnect). Never throws and never surfaces an
    // error or a chooser/button — it only ever helps the real reconnect-wait resolve sooner.
    attemptSilentPostOtaReconnect(): Promise<void> {
        return this.device?.attemptSilentPostOtaReconnect() ?? Promise.resolve();
    }

    // Owns the whole post-reset reconnect window on web (quiet period + raw-GATT poll loop) —
    // see PhysensBleDevice.attemptPostRebootReconnect. Resolves true only once the device is
    // genuinely back at READY_FOR_PACKETS.
    attemptPostRebootReconnect(quietMs?: number, windowMs?: number, intervalMs?: number, gattTimeoutMs?: number): Promise<boolean> {
        return this.device?.attemptPostRebootReconnect(quietMs, windowMs, intervalMs, gattTimeoutMs) ?? Promise.resolve(false);
    }

    // Driven by the "Tap to finish" button once attemptPostRebootReconnect()'s window elapses
    // without success — see PhysensBleDevice.finishManualTapReconnect.
    finishManualTapReconnect(): Promise<boolean> {
        return this.device?.finishManualTapReconnect() ?? Promise.resolve(false);
    }

    // See PhysensDevice.readChargeStateForOta (bug 20264).
    readChargeStateForOta(): Promise<BLE_CHARGE_ICON | null> {
        return this.device?.readChargeStateForOta() ?? Promise.resolve(null);
    }

    // Stashes the confirmed-pre-reboot OTA target version so the read-only check below can run
    // on the next successful connect even if the component that initiated the update never sees
    // the reconnect itself (route torn down, ambiguous ~90s timeout, etc.). Never re-sends
    // cmdImageConfirm — the image was already made permanent before the reset was sent.
    private static readonly PENDING_OTA_VERIFY_TARGET_KEY = 'ota.pendingVerifyTarget';
    // How long a stashed verify target is trusted before it's treated as abandoned. Without
    // this, a stash left behind by a run that never reached success/failed (e.g. the tab was
    // closed mid-update) could sit in localStorage indefinitely and get picked up by an
    // unrelated future connect, wrongly flagging that connect's device as a failed update.
    private static readonly PENDING_OTA_VERIFY_TARGET_MAX_AGE_MS = 10 * 60 * 1000;

    setPendingOtaVerifyTarget(targetVersionRaw: string | undefined) {
        if (!targetVersionRaw) return;
        try {
            localStorage.setItem(DatasnapDevice.PENDING_OTA_VERIFY_TARGET_KEY, JSON.stringify({ targetVersion: targetVersionRaw, deviceId: this.deviceId, storedAt: Date.now() }));
        } catch {}
    }

    private clearPendingOtaVerifyTarget() {
        try { localStorage.removeItem(DatasnapDevice.PENDING_OTA_VERIFY_TARGET_KEY); } catch {}
    }

    // Normalizes MCUboot "0.5.4+0" / Zephyr "0.5.4.0" / "v0.5.4" to "0.5.4.0" for equality.
    private normalizeOtaVersion(v: string | undefined): string | null {
        if (!v) return null;
        const m = v.trim().replace(/^v/i, '').match(/^(\d+)\.(\d+)\.(\d+)(?:[.+](\d+))?/);
        return m ? `${m[1]}.${m[2]}.${m[3]}.${m[4] ?? 0}` : null;
    }

    // Read-only check (fires on every successful connect). If a pending OTA verify target is
    // stashed for THIS device, compares it to the active image (NEVER confirms/writes anything)
    // and updates the out-of-date/failed badge accordingly, then clears the stash either way.
    private async checkPendingOtaVerifyTarget() {
        let raw: string | null = null;
        try { raw = localStorage.getItem(DatasnapDevice.PENDING_OTA_VERIFY_TARGET_KEY); } catch { return; }
        if (!raw) return;
        let stored: { targetVersion?: string; deviceId?: string; storedAt?: number } | null = null;
        try { stored = JSON.parse(raw); } catch { this.clearPendingOtaVerifyTarget(); return; }
        if (!stored?.targetVersion) { this.clearPendingOtaVerifyTarget(); return; }
        // Treat a missing/old-format storedAt the same as expired — never act on a stash we
        // can't prove is fresh.
        if (!stored.storedAt || Date.now() - stored.storedAt > DatasnapDevice.PENDING_OTA_VERIFY_TARGET_MAX_AGE_MS) {
            console.warn(`[FWUpdate] pending-verify (post-reconnect): stash for target ${stored.targetVersion} is stale (age=${stored.storedAt ? Date.now() - stored.storedAt : 'unknown'}ms) — ignoring, not flagging failure`);
            this.clearPendingOtaVerifyTarget();
            return;
        }
        // Only ever act on the device the target was stored for.
        if (stored.deviceId && this.deviceId && stored.deviceId !== this.deviceId) return;
        // Nothing may be sent to a device on the charger (bug 20264). A charging reply is
        // published on battery_state_subject, which makes DatasnapDeviceService disconnect.
        const chargeState = await this.device?.readChargeStateForOta();
        if (chargeState != null && isChargingState(chargeState)) return;
        try {
            const imageState = await this.device?.mcumgr?.cmdImageState();
            const active = imageState?.images.find((i: any) => i.active) ?? imageState?.images?.[0];
            this.clearPendingOtaVerifyTarget();
            if (!active) return;
            const matched = this.normalizeOtaVersion(active.version) === this.normalizeOtaVersion(stored.targetVersion) && !!active.confirmed;
            if (matched) {
                console.log(`[FWUpdate] pending-verify (post-reconnect): target ${stored.targetVersion} confirmed active — update verified`);
                this.updateFailed = false;
                this.isFWOutOfDate = false;
                this.updateChecked = true;
            } else {
                console.warn(`[FWUpdate] pending-verify (post-reconnect): active=${active.version} confirmed=${active.confirmed} != target ${stored.targetVersion} — update did not apply`);
                this.updateFailed = true;
                this.updateChecked = true;
            }
        } catch (e) {
            console.warn('[FWUpdate] pending-verify (post-reconnect): failed', e);
        }
    }

    // Forwarders for the OTA verify flow's loop-pause control (see
    // PhysensBleDevice.pauseAutoReconnectForVerify).
    get autoReconnectPaused(): boolean { return this.device?.autoReconnectPaused ?? false; }
    pauseAutoReconnectForVerify() { this.device?.pauseAutoReconnectForVerify(); }
    resumeAutoReconnectForVerify() { this.device?.resumeAutoReconnectForVerify(); }

    async retryConnection() {
        this.device?.resetConnectionAttempts();
        // Clear out the stale/half-open GATT session left behind by the prior failed
        // connection attempts before reconnecting — matches what the automatic background
        // reconnect loop does; skipping it left manual reselect retries silently timing out.
        await this.device?.cleanupStaleConnection();
        this.device?.connect(4);
    }

    reselectDevice(device: PhysensBleDevice | null = null) {
        if(device) {
            const oldDevice = this.device;
            this.cleanUpSubscriptions();
            // Dispose the outgoing instance so its reconnect timers/listeners stop —
            // otherwise it keeps managing this deviceId in the background even though
            // this wrapper (and idlService's registry) has moved on to the new instance.
            if(oldDevice && oldDevice !== device) {
                oldDevice.remove();
            }
            this.device = device;
            this.device.updateInProgress = this._currentlyUpdating;
            this.setupSubscriptions();
        } else {
            return;
        }
    }

    deviceColor: string | null = null;
    deviceColorRGB: {red:number;green:number;blue:number} | null = null;
    
    updateChecked: boolean = false;
    isFWOutOfDate: boolean = false;
    updateFailed: boolean = false;

    // The in-flight OTA state-machine run (reset -> disconnect -> reconnect -> verify),
    // or null when no update is running. Lives on the DEVICE, not the firmware-update
    // component — on purpose: the firmware-update route is non-reusable, so a reconnect
    // mid-update tears down the component and builds a brand-new one, which would
    // otherwise re-run startUpdate() from scratch and race the original run's own
    // reset/reconnect/verify sequence. A fresh component instance that finds this set
    // awaits the SAME promise and mirrors its real result instead of restarting (or
    // worse, trusting a pre-flight image-state read that can look "already up to date"
    // mid-swap, before the device has actually finished rebooting).
    fwUpdateInFlight: Promise<'success' | 'installed' | 'manualSelectRequired'> | null = null;

    // Exposes PhysensBleDevice's last-used reconnect strategy (watchAdvertisements /
    // plain connect / manual reselect) purely for the firmware-update state machine's
    // [FWUpdate] RECONNECTING log line — not used for any control flow.
    get reconnectMethod(): string | null { return this.device?.reconnectMethod ?? null; }

    private _currentlyUpdating: boolean = false;
    get currentlyUpdating(): boolean { return this._currentlyUpdating; }

    // Hard-stops an in-flight OTA (bug 20264: device placed on charger). mcumgr.abort() makes
    // the transport refuse every further write; the update flags are then cleared directly,
    // NOT through the currentlyUpdating setter — that setter would lift the PhysensDevice's
    // updateInProgress gate and run connect housekeeping (setDate/setTime/...), i.e. send more
    // data. The PhysensDevice keeps updateInProgress=true so its NUS queue stays gated until the
    // caller tears it down; a later reselect gets a fresh instance with a clean state.
    abortUpdate(reason: string) {
        this.device?.mcumgr?.abort(reason);
        this._currentlyUpdating = false;
        this.rebootInProgress = false;
        this.otaVerifying = false;
        this.fwUpdateInFlight = null;
        this.clearPendingOtaVerifyTarget();
    }

    set currentlyUpdating(value: boolean) {
        const wasUpdating = this._currentlyUpdating;
        this._currentlyUpdating = value;
        // Keep the underlying device's polling in sync so keepalive/battery/file-recorder
        // requests don't compete with firmware update traffic on the same BLE link.
        if (this.device) {
            this.device.updateInProgress = value;
            if (value && !wasUpdating) {
                this.device.cancelPendingHousekeeping();
                // A fresh update start makes any earlier stash irrelevant — it either already
                // resolved or belongs to a run that never finished; either way this run will
                // stash its own target before rebooting if it gets that far.
                this.clearPendingOtaVerifyTarget();
            } else if (!value && wasUpdating) {
                // Only the two terminal outcomes (success/failed) flip currentlyUpdating back to
                // false before the user taps Okay — the ambiguous installed/manualSelectRequired
                // outcomes leave it true on purpose, since the stash is exactly what lets a later
                // connect (possibly a different session) still verify them. So clearing it here
                // only ever discards a stash whose run has truly concluded.
                this.clearPendingOtaVerifyTarget();
                if (this.device.deviceState === Device_State.READY_FOR_PACKETS) {
                    // No further READY_FOR_PACKETS transition happens once the device is
                    // already connected post-update — run the same one-time housekeeping
                    // a fresh connect would have, now that it's safe to use the write queue.
                    this.runConnectHousekeeping(this.deviceColorRGB ?? DATASNAP_DEVICE_COLORS[0]);
                }
            }
        }
    }

    private _rebootInProgress: boolean = false;
    get rebootInProgress(): boolean { return this._rebootInProgress; }
    set rebootInProgress(value: boolean) {
        this._rebootInProgress = value;
        // Narrower than currentlyUpdating/updateInProgress: only true for the actual
        // reset->disconnect->reconnect window owned by firmwareupdate.component's
        // rebootAfterConfirm(), so a disconnect that happens earlier (mid-upload) is still
        // governed by the normal reconnect-retry safety nets instead of being exempted
        // for the whole update's duration.
        if (this.device) {
            this.device.expectingReboot = value;
        }
    }

    private _otaVerifying: boolean = false;
    get otaVerifying(): boolean { return this._otaVerifying; }
    set otaVerifying(value: boolean) {
        this._otaVerifying = value;
        // Narrower still than rebootInProgress: only true for the read-only post-reconnect
        // verify window, after rebootInProgress has already been cleared — see
        // PhysensDevice.otaVerifying for why PhysensBleDevice.connect() needs this as a
        // separate exclusion from expectingReboot.
        if (this.device) {
            this.device.otaVerifying = value;
        }
    }

    recording: boolean = false;
    recordingCallTimeoutSubject: Subject<void> = new Subject<void>();
    private recordingCallTimeoutSubscription: Subscription | null = null;
    private timerSubscriptions: Subscription[] = [];

    onRemoveSubject: Subject<void> = new Subject<void>();

    // Stable across reselectDevice() swapping the underlying PhysensBleDevice instance —
    // unlike deviceStateSubject (a plain passthrough getter to the current device's own
    // Subject), anyone who subscribes to this once keeps receiving state updates even
    // after a mid-wait reselect swaps in a new device instance. firmwareupdate.component's
    // waitForDeviceReboot() captures a device-state subject once at the start of a long
    // wait that can span a reselect — without this, a reselect left it subscribed to the
    // abandoned instance's dead Subject, so the "reboot complete" promise never resolved
    // even though the device had actually reconnected (the page stayed stuck at 95%).
    deviceStateChangeSubject: BehaviorSubject<Device_State | undefined> = new BehaviorSubject<Device_State | undefined>(undefined);

    // Stable across reselectDevice() the same way deviceStateChangeSubject is — forwards
    // PhysensBleDevice's staleAddressReconnectFailureSubject so firmwareupdate.component's
    // waitForDeviceReboot() can keep counting failures across a mid-wait device swap.
    staleAddressReconnectFailureSubject: Subject<void> = new Subject<void>();

    // Stable across reselectDevice() the same way the subjects above are — forwards
    // PhysensBleDevice's otaReconnectStuck so firmwareupdate.component can show its own
    // tap-to-reconnect screen once the mid-upload 2-strikes teardown has run, surviving a
    // reselect mid-OTA the same way staleAddressReconnectFailureSubject does.
    otaReconnectStuck: Subject<void> = new Subject<void>();

    downloadStatus: {
        fileName: string;
        percentage: number;
        data?: Uint8Array;
    }|null = null;

    errorMessage: string | null = null;
    canFreeUpStorage: boolean = false;
    isFreeingUpStorage: boolean = false;
    isRebooting: boolean = false;

    parsedData: {
        connectionStats: ConnectionStatistics;
        publishers: Publisher[];
        device_info: any;
        parsedData: ParsedValues;
    } | null = null;

    results: ImpactResults | AirRacerResults | ProjectileResults | null = null;
    getResultsAny = () => this.results as any|null;
    //{
//     impact_G: 85.3,
//     impact_m_per_s2: 836.3,
//     impact_N: 22.4,
//     max_velocity: 11.2,
//     flight_time: 1.53,
//     drop_height_m: 1.15,
//     egg_survival: true,
//     data: [
//       { timestamp: 0, accel_magnitude: 9.81, velocity: 0 },
//       { timestamp: 100, accel_magnitude: 9.79, velocity: 0.2 },
//       { timestamp: 200, accel_magnitude: 9.78, velocity: 0.45 },
//       { timestamp: 300, accel_magnitude: 9.82, velocity: 0.71 },
//       { timestamp: 400, accel_magnitude: 9.80, velocity: 0.92 },
//       { timestamp: 500, accel_magnitude: 8.00, velocity: 1.30 },
//       { timestamp: 600, accel_magnitude: 0.00, velocity: 3.50 },
//       { timestamp: 700, accel_magnitude: 0.00, velocity: 7.20 },
//       { timestamp: 800, accel_magnitude: 0.00, velocity: 10.5 },
//       { timestamp: 900, accel_magnitude: 836.3, velocity: 0.0 }, // impact
//       { timestamp: 1000, accel_magnitude: 12.1, velocity: 0.0 },
//       { timestamp: 1100, accel_magnitude: 9.9, velocity: 0.0 },
//       { timestamp: 1200, accel_magnitude: 9.81, velocity: 0.0 },
//     ],
//     timeline: {
//       hold_start: 0,
//       hold_end: 300,
//       freefall_start: 300,
//       freefall_end: 800,
//       impact: 900,
//       settling_start: 1000,
//       settling_end: 1200,
//     },
//   };

    constructor(device: PhysensBleDevice, private analysisServer: DatasnapAnalysisService, public unitId: string, private  rwUpdateServerService: RWUpdateServerService, private ccService: ConsoleCaptureService) {
        this.device = device;

        this.analysisServer.prepareScopes(unitId);

        this.setupSubscriptions();
    }

    setupSubscriptions() {
        //TODO: start subscribing to device subjects here
        this.device?.deviceStateSubject.subscribe((state)=>{
            this.deviceStateChangeSubject.next(state);
            if(state == Device_State.READY_FOR_PACKETS) {
                //determine color
                // const ledIndex = this.extractLowerNibble(this.device?.deviceName??"0000")??0
                const ledIndex = Math.floor(Math.random() * DATASNAP_DEVICE_COLORS.length)
                const ledColor = DATASNAP_DEVICE_COLORS[ledIndex]
                this.deviceColorRGB = ledColor
                this.deviceColor = this.rgbToHex(ledColor);

                this.checkPendingOtaVerifyTarget();

                // Shares the BLE write queue with the mcumgr OTA chunk writes — skip it
                // while an update is in progress so it can't strand a packet behind
                // updateInProgress's gate in PhysensDevice.clearPacketBuffer(). Runs once
                // from the currentlyUpdating setter instead, right after the update ends.
                if(!this.device?.updateInProgress) {
                    this.runConnectHousekeeping(ledColor);
                }
            }
        })

        this.device?.connectAttemptLimitSubject.subscribe(()=>{
            //Timed out trying to connect
            this.setStatus(DatasnapDeviceStatus.CONNECTION_TIMEOUT);
        });
        this.device?.staleAddressReconnectFailureSubject.subscribe(()=>{
            this.staleAddressReconnectFailureSubject.next();
        });
        this.device?.otaReconnectStuck.subscribe(()=>{
            this.otaReconnectStuck.next();
        });
        this.device?.fileRecorderStateSubject.subscribe((state)=>{
            if(state == FILERECORDER_STATE.RCORDING) {
                this.setStatus(DatasnapDeviceStatus.RECORDING);
            }
            if(state == FILERECORDER_STATE.READY) {
                this.setStatus(DatasnapDeviceStatus.OFFLOADING);
            }
        });

        // Start the recording call timeout timer
        this.resetRecordingCallTimeout();
    }

    // The one-time "just connected" housekeeping (clock sync, LED, FW-update check,
    // system status). Normally fires from setupSubscriptions() on READY_FOR_PACKETS;
    // also re-run once from the currentlyUpdating setter when an update ends, since no
    // further READY_FOR_PACKETS transition happens once the device is already connected.
    private runConnectHousekeeping(ledColor: {red:number;green:number;blue:number}) {
        var now = new Date()
        this.device?.setTime(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds())
        this.device?.setDate(now.getFullYear()-2000, now.getMonth() + 1, now.getDate(), now.getDay())

        this.device?.setLEDColor(ledColor)

        this.checkFWUpdate().catch((err) => {
            if (err?.message !== DatasnapDevice.OTA_CANCEL_REASON) throw err;
        });

        this.device?.getSystemStatus().then((resp)=>{
            if(resp?.[0].mPktSubType == PACKET_SUB_TYPE.GET_SYSTEM_STATUS) {
                let statuses = resp[0].systemStatusList

                if(statuses) {
                    for(let i = 0; i < statuses.length; i++) {
                        let status = statuses[i];
                        console.log(`Subsystem status (${i}) - state: 0x${status.state.toString(16).padStart(2, '0')}, detail: 0x${status.detail.toString(16).padStart(2, '0')}, info: 0x${status.info.toString(16).padStart(4, '0')}`);
                    }
                }
            } else {
                console.error("Unexpected response to getSystemStatus command: ", resp);
            }
        }).catch((err)=>{
            if (err?.message === DatasnapDevice.OTA_CANCEL_REASON) return;
            console.error("Failed to get system status: ", err);
        });
    }

    // Mirrors PhysensDevice's own OTA-cancel rejection reason (see
    // PhysensDevice.cancelPendingHousekeeping) so getSystemStatus's external .catch
    // above can recognize the same cancellation and stay quiet.
    private static readonly OTA_CANCEL_REASON = 'OTA update starting';

    cleanUpSubscriptions() {
        this.device?.deviceStateSubject.unsubscribe();
        this.device?.connectAttemptLimitSubject.unsubscribe();
        this.device?.fileRecorderStateSubject.unsubscribe();
        this.device?.staleAddressReconnectFailureSubject.unsubscribe();
        this.device?.otaReconnectStuck.unsubscribe();

    }

    async checkFWUpdate() {
        await this.device?.getDevInfo()
        const isOutOfDate = await this.rwUpdateServerService.isFWOutOfDate({
            versionMajor: this.device!.device_info?.fwMajorVersion ?? 0,
            versionMinor: this.device!.device_info?.fwMinorVersion ?? 0,
            versionBug: this.device!.device_info?.fwBugVersion ?? 0,
            versionSpecial: 0
        });

        this.updateChecked = true;
        this.isFWOutOfDate = isOutOfDate;
    }

    notify() {
        console.log("Notifying device "+this.device?.deviceName)
        this.device?.identifyDevice({...this.deviceColorRGB??{red:255,green:255,blue:255}, durationUs: 1000000, blinkRate: 100});
    }

    extractLowerNibble(input: string): number | null {
        const match = input.match(/[0-9A-Fa-f]{4}$/);
        if (!match) return null;

        const hexPart = match[0];
        const lastTwoHex = hexPart.slice(2);

        const value = parseInt(lastTwoHex, 16);

        // Keep only the lower nibble (0–15)
        return value & 0xF;
    }

    rgbToHex(color: { red: number; green: number; blue: number }): string {
        const { red, green, blue } = color;

        // Clamp each value to 0–255, then convert to 2-digit hex
        const r = Math.max(0, Math.min(255, red)).toString(16).padStart(2, "0");
        const g = Math.max(0, Math.min(255, green)).toString(16).padStart(2, "0");
        const b = Math.max(0, Math.min(255, blue)).toString(16).padStart(2, "0");

        return `#${r}${g}${b}`.toUpperCase();
    }

    setStatus(newStatus: DatasnapDeviceStatus) {
        if(newStatus == this.status) {
            return;
        }
        
        switch(newStatus) {
            case DatasnapDeviceStatus.RECORDING:
                if (this.status != DatasnapDeviceStatus.IDLE) {
                    return;
                }
                break;
            case DatasnapDeviceStatus.OFFLOADING:
                if (this.status != DatasnapDeviceStatus.RECORDING) {
                    return;
                }
                this.getRecording();
                break;
        }
        this.status = newStatus;
    }

    startRecording(subs: DeviceFileSubscription[]) {
        this.resetRecordingCallTimeout();
        this.device?.subscribePublishers(subs, PORT_UUID.FILE0);
        console.log(`[BLE][${this.device?.deviceName ?? this.device?.deviceId}] recorder enabled=true requested at ${new Date().toISOString()}`)
        console.log("Sending enable filerecord for "+this.device?.deviceName)
        this.device?.enableFileRecorderV2(`datasnap`, true, 30000, 200*1024).then((resp)=>{
            if(resp[0]?.mPktSubType == PACKET_SUB_TYPE.ENABLE_FILERECORDER_V2) {
                if(resp[0].result != 0) {
                    console.error("Failed to enable file recorder on device "+this.device?.deviceName+", device responded with error code: ", resp[0].result);
                    this.pullDebugLog();
                    this.errorMessage = "Unable to start recording (device storage may be full)";
                    this.canFreeUpStorage = true;
                    this.setStatus(DatasnapDeviceStatus.ERROR_OFFLOADING);
                } else {
                    console.log("File recorder enabled successfully on device "+this.device?.deviceName)

                }
            } else {
                console.error("Failed to enable file recorder on device "+this.device?.deviceName+", unexpected response: ", resp)
                this.errorMessage = "Unable to start recording";
                this.canFreeUpStorage = true;
                this.setStatus(DatasnapDeviceStatus.ERROR_OFFLOADING);
            }
        }).catch((err)=>{
            console.error("Failed to start file recorder: ", err);
            this.pullDebugLog();
            this.errorMessage = "Unable to start recording";
            this.canFreeUpStorage = true;
            this.setStatus(DatasnapDeviceStatus.ERROR_OFFLOADING);
        });

        // Poll file recorder states at 1 Hz
        // if ((this as any)._fileRecorderInterval) {
        // clearInterval((this as any)._fileRecorderInterval);
        // }
        // (this as any)._fileRecorderInterval = setInterval(() => {
        //     this.getFileRecorderStates();
        // }, 1000);
        // this.recording = true
        // this.setStatus(DatasnapDeviceStatus.RECORDING);
    }

    // getFileRecorderStates = () => {
    //     this.device?.getFileRecorderStatus(PORT_UUID.FILE0);
    // }

    stopRecording() {
        this.resetRecordingCallTimeout();
        this.device?.unsubscribeAll(PORT_UUID.FILE0);
        console.log(`[BLE][${this.device?.deviceName ?? this.device?.deviceId}] recorder enabled=false requested at ${new Date().toISOString()}`)
        console.log("Sending disable filerecord for "+this.device?.deviceName)
        this.device?.enableFileRecorderV2(``, false, 30000, 200*1024);
        // if ((this as any)._fileRecorderInterval) {
        // clearInterval((this as any)._fileRecorderInterval);
        //     (this as any)._fileRecorderInterval = undefined;
        // }
        // this.getFileRecorderStates();
        // this.recording = false
        // this.setStatus(DatasnapDeviceStatus.OFFLOADING);
        
        // this.getRecording();
    }

    parseFilenameToDate(filename: string): Date | null {
        // Match patterns like "YYMMDD_HHMMSS"
        const match = filename.match(/^(\d{6})_(\d{6})_/);
        if (!match) return null;

        const [_, datePart, timePart] = match;

        const year = parseInt(datePart.slice(0, 2), 10);
        const month = parseInt(datePart.slice(2, 4), 10) - 1; // JS months are 0-based
        const day = parseInt(datePart.slice(4, 6), 10);
        const hour = parseInt(timePart.slice(0, 2), 10);
        const minute = parseInt(timePart.slice(2, 4), 10);
        const second = parseInt(timePart.slice(4, 6), 10);

        // Assume years 2000–2099
        const fullYear = year + 2000;

        // Create and validate the Date
        const date = new Date(fullYear, month, day, hour, minute, second);
        if (isNaN(date.getTime())) return null;

        return date;
        }

    async getRecording() {
        
        try {
            console.log("getting drive list")
            const response = await this.device?.filesystemGetDriveList();
            const drive = response?.[0].fileSystemDrives?.find((d) => d.path === "/raw");
            // const drive = response?.[0].fileSystemDrives?.[0];
            if(!drive) {
                console.error("Did not find any drives on the device filesystem")
                this.errorMessage = "No recordings found (recording may have been lost on disconnect)";
                this.setStatus(DatasnapDeviceStatus.ERROR_OFFLOADING);
                return
            }

            const driveIdx = drive.index
            const dirPath = drive.path

            const driveItems = await this.device?.mapDirectory(driveIdx, dirPath) ?? []
            console.log(`[BLE][${this.device?.deviceName ?? this.device?.deviceId}] file list result: ${driveItems.length} item(s) found in ${dirPath}`)
            let latestItem: Drive_Item|null = null
            let latestDate: Date|null = null
            for(let ditem of driveItems) {
                const date = this.parseFilenameToDate(ditem.name);
                if (!date) continue; // skip files that don’t match pattern

                console.log("found file:", ditem.name);

                if (!latestDate || date > latestDate) {
                    latestDate = date;
                    latestItem = ditem;
                }
            }
            const fileItem = latestItem;
            if(!fileItem) {
                console.error("Did not find any drive on the device filesystem")
                console.log(`[BLE][${this.device?.deviceName ?? this.device?.deviceId}] file list result: no matching recording file among ${driveItems.length} item(s)`)
                this.errorMessage = "No recordings found (recording may have been lost on disconnect)";
                this.setStatus(DatasnapDeviceStatus.ERROR_OFFLOADING);
                return;
            }
            console.log(`[BLE][${this.device?.deviceName ?? this.device?.deviceId}] file list result: selected latest recording ${fileItem.name}`)


            this.downloadStatus = {
                fileName: fileItem.name,
                percentage: 0
            };
            const startTime = performance.now();

            const temp1 = await Datasnap_MK1.getFileMetadata(this.device!, driveIdx, fileItem.path!)
            let fileMetadataResponse = temp1 ? temp1[0] : null;
            const metadata = fileMetadataResponse ? Datasnap_MK1.fileMetadataFromAppResponsePacket(fileMetadataResponse) : null;

            this.ccService.log(`Starting file download: ${fileItem.name}, size: ${fileItem.size} bytes, stored hash in metadata: ${metadata?.storedHash}, file metadata: ${JSON.stringify(metadata)}`);
            
            this.device?.readFileWithProgress(driveIdx, fileItem).subscribe({
                next: (progress) => {
                    this.downloadStatus!.percentage = progress.progress;
                    console.log(`Download progress: ${progress.progress}%`);
                    if(progress.data) {
                        this.downloadStatus!.data = progress.data;
                        console.log("Average response time:", progress.averageResponseTime, "ms");
                        console.log("Average command time:", progress.averageCommandTime, "ms");
                        // console.log(String.fromCharCode(...progress.data));
                    }
                    if(progress.hashMatch !== undefined) {
                        console.log("File hash match:", progress.hashMatch);

                        //do stuff here?
                        this.pullDebugLog();

                        this.logRawFlashStats(driveIdx);

                    }
                }, 
                error: (err) => {
                    console.error("Download failed:", err);
                    if(fileItem.path != null) {
                        //Regardless of error, try to delete the file to prevent filling up the device storage with failed recordings
                        this.device?.deleteFile(driveIdx, fileItem.path)
                    }
                    this.setStatus(DatasnapDeviceStatus.ERROR_OFFLOADING);
                },
                complete: () => {
                    console.log("Download completed");
                    const endTime = performance.now();
                    const duration = endTime - startTime;
                    console.log(`Download completed in ${duration.toFixed(2)} ms`);
                    const rateKBps = (fileItem.size / 1024) / (duration / 1000);
                    console.log("Average rate is: ", rateKBps.toFixed(2), " kB/s");
                    this.downloadStatus!.percentage = 100;

                    this.setStatus(DatasnapDeviceStatus.PARSING);
                    if(fileItem.path != null) {
                        this.device?.deleteFile(driveIdx, fileItem.path)
                    }
                    
                    this.parseDataset()
                }
            })

        } catch (error) {
            console.error("Error transfering file:", error);
            this.setStatus(DatasnapDeviceStatus.ERROR_OFFLOADING);
            return;
        }
        
        
    }



    async pullDebugLog() {
        let logCount = 1;
        while (logCount > 0) {
            let rawResp = await Datasnap_MK1.getLogBufferPop(this.device!);
            if(!rawResp) {
                console.error("Failed to get log buffer pop response");
                return;
            }
            let [respPkt, cmdPkt, stats] = rawResp;

            if(respPkt.mPktSubType !== Datasnap_MK1.APP_CMD_TYPE.LOG_BUFFER_POP) {
                console.error("Received unexpected packet subtype when expecting LOG_BUFFER_POP: ", respPkt.mPktSubType);
                return;
            }

            const logEntry = Datasnap_MK1.logBufferPopFromAppResponsePacket(respPkt);
            logCount = logEntry?.entry_count??0;
            console.log(`Debug Log Entry #${logCount}: ${logEntry?.entry_string}`);
            console.log(`Log bytes available: ${logEntry?.bytes_available}, drop count: ${logEntry?.drop_count}, bytes popped: ${logEntry?.bytes_popped}`);
            console.log(`Log buffer - ${logEntry?.entry_string}`)
        }
    }

    async logRawFlashStats(driveIdx: number) {
        Datasnap_MK1.getRawFlashStats(this.device!, driveIdx)?.then(([appPkt, commandPkt, stats]) => {
            if(appPkt.mPktSubType !== Datasnap_MK1.APP_CMD_TYPE.GET_RAW_FLASH_STATS) {
                console.error("Received unexpected packet subtype when expecting GET_RAW_FLASH_STATS: ", appPkt.mPktSubType);
                return;
            }

            const flashStats = appPkt ? Datasnap_MK1.rawFlashStatsFromAppResponsePacket(appPkt) : null;

            if(flashStats) {
                let formattedFlashStats = Datasnap_MK1.formatRawFlashDriveStats(flashStats);
                formattedFlashStats.forEach((stat) => {
                    this.ccService.log(`Flash Stat - ${stat}`);
                }); 
            } else {
                console.warn("Failed to get flash stats");
            }
        });

    }

    parseDataset() {
        if(this.downloadStatus?.percentage != 100){ return; }

        const recordingData = this.downloadStatus?.data;

        if(!recordingData) {return;}

        //parse the data
        console.log(`Length of file is ${recordingData.buffer.byteLength} bytes`)
        let data: {
            connectionStats: ConnectionStatistics;
            publishers: Publisher[];
            device_info: any;
            parsedData: ParsedValues;
        } | undefined = undefined;
        parseSNSBytes(recordingData).subscribe({
            next: (progressData) => {
                data = progressData.parsedData;
            },
            error: (err) => {
                console.error("Parsing failed:", err);
                this.setStatus(DatasnapDeviceStatus.ERROR_OFFLOADING);
            },
            complete: () => {
                console.log("Parsing completed");
                if(!data) {
                    console.error("No data parsed");
                    this.setStatus(DatasnapDeviceStatus.ERROR_OFFLOADING);
                    return;
                }
                console.log(JSON.stringify(data.connectionStats))
                // console.log(data.parsedData)
                //done parsing data

                this.parsedData = data;

                this.setStatus(DatasnapDeviceStatus.CALCULATING)
                this.calculateData()
            }

        })
        
    }

    calculateData() {
        if(!this.parsedData?.parsedData) return;

        console.log("starting analysis")
        const analysisTimeout = timer(30000).subscribe(() => {
            console.error("Analysis timed out");
            this.setStatus(DatasnapDeviceStatus.ERROR_OFFLOADING);
        });
        this.analysisServer.runAnalysis(this.unitId, this.parsedData.parsedData).then((results: ImpactResults | AirRacerResults | ProjectileResults | null)=> {
            this.results = results
            // console.log("Analysis Results:")
            // console.log(results)
        }).catch((err) => {
            console.error("Analysis failed:", err);
            this.results = null;
        }).finally(()=>{
            analysisTimeout.unsubscribe();
            console.log("Finished analysis")
            console.log(this.results)
            this.unitId == "impact"
            // Check if results are valid - be permissive with max_velocity to handle edge cases
            // where analysis might return 0 or small values at different viewport widths
            const isImpactValid = this.unitId == "impact" &&
                (this.results as ImpactResults | null)?.max_velocity !== undefined &&
                (this.results as ImpactResults | null)?.max_velocity !== null &&
                !isNaN((this.results as ImpactResults).max_velocity);

            const isAirRacerValid = this.unitId == "air_racer" &&
                (this.results as AirRacerResults | null)?.isSuccess === true;

            const isProjectileValid = this.unitId == "projectile" &&
                (this.results as ProjectileResults | null)?.isSuccess === true;

            if(
                this.results == null ||
                (this.unitId == "impact" && !isImpactValid) ||
                (this.unitId == "air_racer" && !isAirRacerValid) ||
                (this.unitId == "projectile" && !isProjectileValid)
            ) {
                console.error("Analysis validation failed:", {
                    unitId: this.unitId,
                    results: this.results,
                    isImpactValid,
                    isAirRacerValid,
                    isProjectileValid
                });
                this.setStatus(DatasnapDeviceStatus.ERROR_CALCULATING)
                return;
            }

            this.setStatus(DatasnapDeviceStatus.DISPLAYING)
        })  
    }

    downloadRawData() {
        if(Capacitor.getPlatform() != 'web') {
            console.warn("Download raw data not implemented for this platform");
            return;
        }

        if(this.parsedData == null) {
            console.error("No parsed data available for this device");
            return;
        }

        for(const [key, value] of Object.entries(this.parsedData.parsedData)) {
            const pubId = Number(key);

            const pubInfo = this.parsedData.publishers.find((pub)=>pub.pubId==pubId);
            if(pubInfo == null) {
                console.error("No publisher info for id: ", pubId);
                return;
            }

            this.downloadFile(value, pubInfo)
        }
    }

    downloadAnalyzedData() {
        if(Capacitor.getPlatform() != 'web') {
            console.warn("Download analyzed data not implemented for this platform");
            return;
        }

        if(this.results == null) {
            console.error("No analysis results available for this device");
            return;
        }


        let csvData = []
        switch(this.unitId) {
            case "impact":
                const impactResults = this.results as ImpactResults;
                csvData.push(["Timestamp (ms)", "Accel (G)", "Velocity (m/s)", "Position (m)"]);
                for(const datum of impactResults.data) {
                    csvData.push([datum.timestamp, datum.accel_magnitude, datum.velocity, datum.position]);
                }
                break;
            case "air_racer":
                const airRacerResults = this.results as AirRacerResults;
                csvData.push(["Timestamp (ms)", "Accel (Gs)", "Velocity (m/s)"]);
                for(const datum of airRacerResults.data) {
                    csvData.push([datum.timestamp, datum.accel, datum.vel]);
                }
                break;
            case "projectile":
                const projectileResults = this.results as ProjectileResults;
                csvData.push(["Timestamp (ms)", "Height (m)", "Velocity X (m/s)", "Velocity Y (m/s)"]);
                for(const data of projectileResults.hgtPlot_m ?? []) {
                    csvData.push([data.time_s, data.hgt_m, data.velocityX, data.velocityZ]);
                }
                break;
        }

        if(csvData.length == 0) {
            console.error("No data to download for analysis results");
            return;
        }
        const csvString = csvData.map(row => row.join(",")).join("\n");

        const blob = new Blob([csvString], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.unitId}_analysis_results.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

    }

    downloadFile(data: ParsedValue[], pub: Publisher) {
        let headers: string[] = ["Marker"];

        for (const output of pub.outputs || []) {
            if (Number(output.length) === 1) {
                headers.push(output.name);
            } else {
                for (let vectorIdx = 0; vectorIdx < output.length; vectorIdx++) {
                    headers.push(output.name + "_" + vectorIdx);
                }
            }
        }
        
        const headerline = headers.join(",") + "\r";
        const rows = data.map((datum: ParsedValue) => {
            let dataList: (string | null)[] = [];
            dataList.unshift(datum.sampleTime_uS.toString());

            datum.values.forEach(outerit => {
                outerit.forEach((item: any)=> {
                    dataList.push(item !== null? item.toString() : null)
                })
            })

            return dataList.join(',')
        });
        const csvString = [headerline, ...rows].join('\n');

        const blob = new Blob([csvString], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.device?.deviceName}_${pub.name}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }

    clearRecording() {
        this.downloadStatus = null;
        this.parsedData = null;
        this.results = null
        this.errorMessage = null;
        this.canFreeUpStorage = false;
        this.isFreeingUpStorage = false;
        this.setStatus(DatasnapDeviceStatus.IDLE);
    }

    async freeUpDeviceStorage() {
        this.isFreeingUpStorage = true;
        try {
            console.log(`[BLE][${this.device?.deviceName ?? this.device?.deviceId}] freeing up device storage: looking up /raw drive`);
            const response = await this.device?.filesystemGetDriveList();
            const drive = response?.[0].fileSystemDrives?.find((d) => d.path === "/raw");
            if(!drive) {
                console.error("Cannot free up storage: no /raw drive found");
                this.errorMessage = "Unable to free up storage (no drive found)";
                return;
            }

            console.log(`[BLE][${this.device?.deviceName ?? this.device?.deviceId}] reformatting drive ${drive.path} (index ${drive.index})`);
            // The reformat response has no parser (result is always null) and the device may reboot
            // or drop the link while erasing, so success is decided by re-listing the drive, not by the ack.
            try {
                const reformatResp = await this.device?.reformatDrive(drive.index);
                const r = reformatResp?.[0];
                console.log(`[BLE] reformat ack: subType=${r?.mPktSubType} result=${r?.result} result_code=${r?.result_code}`);
            } catch (err) {
                console.warn("Reformat ack not received, verifying drive contents instead:", err);
            }

            let remaining = await this.listRawFiles(drive.index, drive.path);
            if(remaining.length > 0) {
                console.warn(`[BLE] ${remaining.length} file(s) left after reformat, deleting individually`);
                for(const file of remaining) {
                    for(let attempt = 1; attempt <= 3; attempt++) {
                        try {
                            await this.device?.deleteFile(drive.index, file.path!);
                            break;
                        } catch (err) {
                            console.warn(`Delete ${file.path} failed (attempt ${attempt}/3):`, err);
                        }
                    }
                }
                remaining = await this.listRawFiles(drive.index, drive.path);
            }

            if(remaining.length > 0) {
                console.error(`Failed to free up device storage: ${remaining.length} file(s) could not be removed:`, remaining.map((f) => f.name));
                this.errorMessage = "Failed to free up device storage";
                return;
            }

            console.log(`[BLE][${this.device?.deviceName ?? this.device?.deviceId}] drive ${drive.path} is empty`);
            this.clearRecording();
        } catch (err) {
            console.error("Failed to free up device storage:", err);
            this.errorMessage = "Failed to free up device storage";
        } finally {
            this.isFreeingUpStorage = false;
        }
    }

    private async listRawFiles(driveIdx: number, dirPath: string): Promise<Drive_Item[]> {
        let lastErr: unknown;
        // Device may still be erasing or reconnecting after a reformat; retry the listing.
        for(let attempt = 1; attempt <= 5; attempt++) {
            try {
                const items = await this.device?.mapDirectory(driveIdx, dirPath) ?? [];
                return items.filter((i) => i.type === DRIVE_ITEM_TYPE.FILE_ITEM);
            } catch (err) {
                lastErr = err;
                await new Promise((res) => setTimeout(res, 2000));
            }
        }
        throw lastErr;
    }


    // Sends the mcumgr OS reset. The ack races the reboot, so success is judged by the link dropping.
    async rebootDevice(): Promise<void> {
        const mcumgr = this.device?.mcumgr;
        if(!mcumgr) throw new Error("Device is not connected");
        this.isRebooting = true;
        try {
            void mcumgr.cmdReset().catch(() => undefined);
            const deadline = Date.now() + 10000;
            while(this.device?.deviceState === Device_State.READY_FOR_PACKETS) {
                if(Date.now() > deadline) throw new Error("Device did not reboot");
                await new Promise((res) => setTimeout(res, 250));
            }
            this.clearTimers();
        } catch (err) {
            this.isRebooting = false;
            throw err;
        }
    }

    clearTimers() {
        this.timerSubscriptions.forEach((subscription) => subscription.unsubscribe());
        this.timerSubscriptions = [];
        this.recordingCallTimeoutSubscription = null;
    }

    onRemove() {
        this.onRemoveSubject.next();
    }

    private resetRecordingCallTimeout() {
        if (this.recordingCallTimeoutSubscription) {
            this.recordingCallTimeoutSubscription.unsubscribe();
            this.timerSubscriptions = this.timerSubscriptions.filter(
                (subscription) => subscription !== this.recordingCallTimeoutSubscription
            );
        }

        this.recordingCallTimeoutSubscription = timer(DatasnapDevice.RECORDING_CALL_TIMEOUT_MS).subscribe(() => {
            if(this.currentlyUpdating) {
                this.resetRecordingCallTimeout();
            } else {
                this.recordingCallTimeoutSubject.next();
            }
        });
        this.timerSubscriptions.push(this.recordingCallTimeoutSubscription);
    }
}

export enum DatasnapDeviceStatus {
    IDLE = 'IDLE',
    CONNECTION_TIMEOUT = 'CONNECTION_TIMEOUT',
    RECORDING = 'RECORDING',
    OFFLOADING = 'OFFLOADING',
    ERROR_OFFLOADING = 'ERROR_OFFLOADING',
    PARSING = 'PARSING',
    CALCULATING = 'CALCULATING',
    ERROR_CALCULATING = 'ERROR_CALCULATING',
    DISPLAYING = 'DISPLAYING',
}

















        //make a big array
        // const allData: DatasnapDatum[] = [];
        // for(let [key, values] of Object.entries(this.parsedData?.parsedData??{})){
        //     let pub = this.parsedData?.publishers.find((oPub)=>oPub.pubId==Number(key))
        //     allData.concat(
        //         values.map((value, index)=>{
        //                 let datum = {
        //                     sampleTime_uS: value.sampleTime_uS
        //                 }
        //                 value.values.forEach((physensDatum, outputset_index)=>{
        //                     physensDatum.forEach((physensVectorDatum, vector_index, array)=>{
        //                         (datum as any)[`${key}_${pub?.outputs[vector_index].name}${array.length>1?("_"+vector_index):""}`] = physensDatum
        //                     })
                            
        //                 })
        //                 return datum
        //             }
        //         )
        //     )
        // }

        

        // //Merge matching timestamps
        // const merged = new Map<number, DatasnapDatum>();

        // for (const d of allData) {
        //     const existing = merged.get(d.sampleTime_uS);
        //     if (existing) {
        //     // Merge numeric keys (besides sampleTime_uS)
        //     for (const [key, value] of Object.entries(d)) {
        //         if (key !== "sampleTime_uS") {
        //         existing[key] = (existing[key] ?? 0) + value;
        //         }
        //     }
        //     } else {
        //     // Clone to avoid mutating input
        //     merged.set(d.sampleTime_uS, { ...d });
        //     }
        // }

        // const collatedData = Array.from(merged.values()).sort((a, b) => a.sampleTime_uS - b.sampleTime_uS);